import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import { getFunctions, httpsCallable } from "firebase/functions";
import { PENDING_PUSH_NAVIGATION_KEY } from "../utils/pushNavigation";

const CURRENT_PUSH_TOKEN_KEY = "vaultquest:currentPushToken";

function isNativePushSupported() {
  return Capacitor.isNativePlatform();
}

function getAppVersion() {
  return (
    process.env.REACT_APP_VERSION ||
    process.env.npm_package_version ||
    ""
  );
}

function getPlatform() {
  return Capacitor.getPlatform() || "unknown";
}

function storeCurrentPushToken(token) {
  if (typeof window === "undefined") return;

  try {
    window.localStorage?.setItem(CURRENT_PUSH_TOKEN_KEY, token);
  } catch {
    // Local storage can be unavailable in restricted browser contexts.
  }
}

function readCurrentPushToken() {
  if (typeof window === "undefined") return "";

  try {
    return window.localStorage?.getItem(CURRENT_PUSH_TOKEN_KEY) || "";
  } catch {
    return "";
  }
}

function clearCurrentPushToken() {
  if (typeof window === "undefined") return;

  try {
    window.localStorage?.removeItem(CURRENT_PUSH_TOKEN_KEY);
  } catch {
    // Local storage can be unavailable in restricted browser contexts.
  }
}

async function callPushTokenCallable(name, payload) {
  const callable = httpsCallable(getFunctions(), name);
  return callable(payload);
}

export async function registerNativePushNotifications() {
  if (!isNativePushSupported()) {
    return { skipped: true, reason: "not_native" };
  }

  let permission = await PushNotifications.checkPermissions();
  if (permission.receive === "prompt") {
    permission = await PushNotifications.requestPermissions();
  }

  if (permission.receive !== "granted") {
    return { skipped: true, reason: "permission_not_granted" };
  }

  await PushNotifications.removeAllListeners();

  await PushNotifications.addListener("registration", async (token) => {
    const value = String(token?.value || "").trim();
    if (!value) return;

    await callPushTokenCallable("registerPushTokenSecure", {
      token: value,
      platform: getPlatform(),
      appVersion: getAppVersion(),
    });

    storeCurrentPushToken(value);
  });

  await PushNotifications.addListener("registrationError", (error) => {
    console.warn("Native push registration failed.", {
      code: error?.code || error?.name || "unknown",
    });
  });

  await PushNotifications.addListener("pushNotificationReceived", (notification) => {
    console.log("Native push received while app is open.", {
      hasData: Boolean(notification?.data),
      hasTitle: Boolean(notification?.title),
      hasBody: Boolean(notification?.body),
    });
  });

  await PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
    const data = action?.notification?.data || {};

    try {
      window.sessionStorage?.setItem(
        PENDING_PUSH_NAVIGATION_KEY,
        JSON.stringify(data)
      );
    } catch {
      // Navigation payload is optional; failing to persist it must not break the app.
    }
  });

  await PushNotifications.register();
  return { success: true };
}

export async function unregisterCurrentNativePushToken() {
  if (!isNativePushSupported()) {
    return { skipped: true, reason: "not_native" };
  }

  const token = readCurrentPushToken();
  if (!token) {
    return { skipped: true, reason: "no_token" };
  }

  try {
    await callPushTokenCallable("unregisterPushTokenSecure", { token });
  } finally {
    clearCurrentPushToken();
  }

  return { success: true };
}
